import { computed, watch } from 'vue';
import { toValue, type MaybeRefOrGetter } from '@vueuse/core';
import { useRoute } from 'vue-router/composables';

import {
	Platform,
	SortOrder,
	SportEventAggregation,
	SportEventStatus,
	SportEventV2,
	SportType,
} from '@/@types/graphql-types';
import { FilteredEvents, TimeGroup } from '@/features/sports/types/sports-types';
import { isSameDate, shiftDateWithTimezone } from '@/helpers/date-helper';
import { SportPageType } from '@/features/sports/types/interfaces';

import {
	GetSingleStepSportOverviewDocument,
	GetSingleStepSportOverviewQuery,
	GetSingleStepSportOverviewQueryVariables,
} from '@/features/sports/graphql/queries/GetSingleStepSportOverview.query';

import { SingleStepContext } from '@/features/sports/helpers/config';
import { usePersistedTodaysDate } from '@/helpers/composables/usePersistedTodaysDate';
import { quadrennialComps } from '@/features/sports/constants';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { useQuery } from '@/helpers/composables/useApollo';

export interface UseSportEventsOptions {
	context: MaybeRefOrGetter<SingleStepContext>;
	competitionId?: MaybeRefOrGetter<string | null>;
	competitorId?: MaybeRefOrGetter<string | null>;
	allowPastEvents?: MaybeRefOrGetter<boolean | null>;
	competitionObjectId?: MaybeRefOrGetter<number | null>;
	ready?: MaybeRefOrGetter<boolean>;
	/**
	 * Pass pre-fetched events to skip the query and prevent waterfall (e.g., from parent query cache).
	 * When provided, the composable will use these events instead of querying, but still apply
	 * all time-grouping, timezone, and event classification logic.
	 */
	providedEvents?: MaybeRefOrGetter<SportEventV2[] | null>;
}

export function useSportEvents(options: UseSportEventsOptions) {
	const route = useRoute();
	const { todayDate } = usePersistedTodaysDate();
	const { language, country, localeObject } = useLanguageStore();

	const context = computed(() => toValue(options.context));
	const competitionId = computed(() => toValue(options.competitionId) ?? null);
	const competitorId = computed(() => toValue(options.competitorId) ?? null);
	const allowPastEvents = computed(() => !!toValue(options.allowPastEvents));
	const competitionObjectId = computed(() => toValue(options.competitionObjectId) ?? null);
	const ready = computed(() => !!toValue(options.ready));
	const providedEvents = computed(() => toValue(options.providedEvents) ?? null);

	const pastCompetitionFrom = computed(() => {
		// limit querying back 4+ years ago to competitions with a 4-year cycle, to reduce the amount of data being processed on BE

		return competitionObjectId.value && quadrennialComps.includes(competitionObjectId.value)
			? new Date('2022-01-01T00:00:00.000Z')
			: new Date('2025-01-01T00:00:00.000Z');
	});

	const startAt = computed(() => {
		// at the moment, from is required for query to work properly.
		// 2023 is an arbitrary date in past that should return past events for all queries, while limiting the amount of data fetched on BE before filtering/limiting
		return allowPastEvents.value
			? { to: todayDate.value.toISOString(), from: pastCompetitionFrom.value }
			: { from: todayDate.value.toISOString() };
	});

	const queryVariables = computed<GetSingleStepSportOverviewQueryVariables>(() => {
		const variables = {
			platform: Platform.Web,
			language: language.value,
			country: country.value,
			first: 1,
			bucketSize: 10,
			groupBy: SportEventAggregation.Sport,
			filter: {
				startAt: startAt.value,
				competitionIds: [] as string[],
				competitorIds: [] as string[],
				sports: [] as SportType[],
			},
			timezoneOffset: 0,
			sortOrder: allowPastEvents.value ? SortOrder.Desc : undefined,
		};

		if (context.value.pageType === SportPageType.SportCompetition) {
			if (competitionId.value) variables.filter.competitionIds = [competitionId.value];
		}
		if (context.value.pageType === SportPageType.SportTeam) {
			if (competitorId.value) variables.filter.competitorIds = [competitorId.value];
		} else {
			if (context.value.sportType) variables.filter.sports = [context.value.sportType];
		}

		return variables;
	});

	const enabled = computed(() => {
		// Skip query if events are provided directly
		if (providedEvents.value !== null && providedEvents.value.length > 0) return false;

		if (route.name === 'app.sports.all' || route.name === 'app.sports.subdomain.all') return true;
		if (route.meta?.data == null) return false;

		return (
			route.meta.data.includes('singlestep') &&
			!(context.value.pageType === SportPageType.SportCompetition && competitionId.value == null) &&
			!(context.value.pageType === SportPageType.SportTeam && competitorId.value == null)
		);
	});

	const { result, loading } = useQuery<GetSingleStepSportOverviewQuery, GetSingleStepSportOverviewQueryVariables>(
		GetSingleStepSportOverviewDocument,
		queryVariables,
		() => ({ enabled: enabled.value })
	);

	const upcomingEvents = computed(() => result.value?.sportEventBucketsV2.edges ?? []);

	const events = computed<SportEventV2[]>(() => {
		// Use provided events if available (bypasses query)
		if (providedEvents.value) {
			return providedEvents.value;
		}

		// Otherwise extract from query result
		return (upcomingEvents.value || [])
			.flatMap(({ node }) => node.edges ?? [])
			.map(({ node }) => node) as SportEventV2[];
	});

	const timeGroupedEvents = computed<FilteredEvents>(() => {
		const filteredEvents: FilteredEvents = { live: [], today: [], upcoming: [], past: [] };

		events.value.forEach((event: any) => filteredEvents[getEventGroup(event)].push(event));

		return filteredEvents;
	});

	const timeGroup = computed<TimeGroup>(() => {
		if (timeGroupedEvents.value.live.length > 0) return 'live';
		if (allowPastEvents.value) return 'past';
		return timeGroupedEvents.value.today.length > 0 ? 'today' : 'upcoming';
	});

	function getEventGroup(event: SportEventV2): TimeGroup {
		const startAt = new Date(event.content.startAt);
		const localStartAt = shiftDateWithTimezone(localeObject.value.timezone, 0, startAt);

		if (event.content.status === SportEventStatus.Live) return 'live';
		if (allowPastEvents.value) return 'past';

		return isSameDate(todayDate.value, localStartAt) ? 'today' : 'upcoming';
	}

	const eventIsLive = (event: SportEventV2) => getEventGroup(event) === 'live';
	const eventIsToday = (event: SportEventV2) => getEventGroup(event) === 'today';
	const eventIsUpcoming = (event: SportEventV2) => getEventGroup(event) === 'upcoming';
	const eventIsPast = (event: SportEventV2) => getEventGroup(event) === 'past';

	watch(ready, (value: boolean) => {
		if (!value) return;

		if (context.value.pageType === SportPageType.SportCompetition && competitionId.value == null) {
			throw Error('[ProvideUpcomingSingleStepEvent] "competitionId" prop is required on competition pages.');
		}

		if (context.value.pageType === SportPageType.SportTeam && competitorId.value == null) {
			throw Error('[ProvideUpcomingSingleStepEvent] "competitorId" prop is required on team pages.');
		}
	});

	const sportEvents = computed(() => ({
		timeGroup: timeGroup.value,
		events: timeGroupedEvents.value[timeGroup.value],
		isDataLoading: loading.value,
		context: context.value,
	}));

	const eventIs = computed(() => ({
		live: eventIsLive,
		today: eventIsToday,
		upcoming: eventIsUpcoming,
		past: eventIsPast,
	}));

	return {
		sportEvents,
		events,
		eventIs,
	};
}
